// eslint:disable max-line-length
import * as React from 'react';
import { MdAdd } from 'react-icons/md';
import { Handlers, HotKeys, KeyMap } from 'ui/common/hotkeys';

import { DefaultValue, createServerValidation, simpleFormNames } from './panel-default-values-utils';
import { FIELD_TYPE } from 'app/infrastructure/simple-form/field-types';
import { ComplexValue } from 'app/infrastructure/ui/form/types';
import { ButtonPrimary, AccessCard, ValidationLevel } from 'ui';
import { AddButton } from 'ui/common/button/add-button';

import { DialogExpander } from 'ui/common/dialog-expander/dialog-expander';
import { OnClickOutside } from 'ui/common/on-click-outside';
import { keyCodeTypes } from 'ui/common/key-code-types';
import { FieldsOptionParams, PanelDefaultValuesProps } from 'app/components/access-panel/default-values/types';
import { mapInvalidOptions } from '../shared';

import styles from './card-value.scss';

export interface CardValueProps extends PanelDefaultValuesProps {
  defaultValue: DefaultValue;
  fieldsOptionParams?: FieldsOptionParams;
  validationErrors?: JSX.Element;
  onChange: (value: ComplexValue<number>[], defaultValue: DefaultValue) => void;
  removeGroup: (defaultValue: DefaultValue) => void;
  handleClickAddButton: () => void;
}

export interface CardValueState {
  showSelectDialog: boolean;
}

export class CardValue extends React.PureComponent<CardValueProps, CardValueState> {
  private keyMap: KeyMap;
  private handlers: Handlers;

  constructor(props: CardValueProps) {
    super(props);
    this.state = { showSelectDialog: false };
    this.keyMap = {
      onEnter: [keyCodeTypes.Enter]
    };

    this.handlers = {
      onEnter: () => this.onSubmit()
    };
  }

  private removeGroup = () => {
    this.props.removeGroup(this.props.defaultValue);
  };

  private onChange = (values: ComplexValue<number>[]) => {
    this.props.onChange(values, this.props.defaultValue);
  };

  private closeSelectDialog = () => {
    this.setState({ showSelectDialog: false });
  };

  private openSelectDialog = () => {
    this.setState({ showSelectDialog: true });
  };

  private onSubmit = () => {
    this.props.handleClickAddButton();
    this.closeSelectDialog();
  };

  private addSelectorContents = () => {
    const { endpoints: {
      defaultValues: {
        optionsCollections,
        caseTypes,
        values,
        valuesFields
      }
    }, defaultValue: {
      caseType,
      optionsColection
    }, fieldsOptionParams: { includeOptionsForAllUserTypes } = {},
      translation, userType, apiToUse } = this.props;

    const SimpleForm = this.props.formComponent;

    return (<>
      <div className="d-none">
        <SimpleForm
          name={simpleFormNames.optionsCollections}
          type={FIELD_TYPE.SELECT_SINGLE_HIERARCHY}
          optionsEndpoint={optionsCollections}
          placeholder={translation.search}
          translation={translation}
          descriptionAllLevels={true}
          label={{ text: translation.code_table, required: false }}
          initialValue={optionsColection}
          readOnly={true}
        />
        <SimpleForm
          name={simpleFormNames.caseTypes}
          type={FIELD_TYPE.SELECT_SINGLE_HIERARCHY}
          optionsEndpoint={caseTypes}
          placeholder={translation.search}
          translation={translation}
          descriptionAllLevels={true}
          label={{ text: translation.case_type, required: false }}
          initialValue={this.props.defaultValue.caseType}
          readOnly={true}
        />
        <SimpleForm
          name={simpleFormNames.values}
          type={FIELD_TYPE.SELECT_SINGLE_HIERARCHY}
          optionsEndpoint={values(optionsColection && optionsColection.value)}
          optionsParams={{ caseTypeId: caseType && caseType.value }}
          placeholder={translation.search}
          translation={translation}
          descriptionAllLevels={true}
          label={{ text: translation.default_value, required: false }}
          initialValue={this.props.defaultValue.defaultValue}
          readOnly={true}
        />
      </div>
      <SimpleForm
        name={simpleFormNames.valuesFields}
        type={FIELD_TYPE.SELECT_MULTIPLE}
        optionsEndpoint={valuesFields(optionsColection && optionsColection.value)}
        optionsParams={{
          includeOptionsForAllUserTypes,
          caseTypeId: caseType && caseType.value,
          userTypeContext: userType
        }}
        placeholder={translation.search}
        apiToUse={apiToUse}
        translation={translation}
        descriptionAllLevels={true}
        label={{ text: translation.field, required: false }}
        initialValue={this.props.defaultValue.defaultValueFields}
        alwaysOpen={true}
        displaySelectAll={true}
      />

      {!!this.props.validationErrors && this.props.validationErrors}

      <div className="d-flex justify-content-end">
        <ButtonPrimary onClick={this.onSubmit} disabled={!!this.props.validationErrors}>{translation.add}</ButtonPrimary>
      </div>
    </>);
  };

  private renderSelectDialog = () => {
    return (
      <DialogExpander selectExpander={true} onClose={this.closeSelectDialog} hasFooter={false}>
        {this.state.showSelectDialog && this.addSelectorContents()}
      </DialogExpander>
    );
  };

  public render() {
    const { validation, readOnly, defaultValue, translation, valueName } = this.props;

    const name = `${defaultValue.optionsColection.value}-${defaultValue.caseType.value}-${defaultValue.defaultValue.value}`;

    const label = <p>
      {defaultValue.caseType.description}<br />
      {defaultValue.optionsColection.description}<br />
      <strong>{defaultValue.defaultValue.description}</strong><br />
    </p>;

    const serverValidation = !readOnly && defaultValue &&
      createServerValidation(defaultValue.defaultValueFields, defaultValue.caseType, validation);
    const cardValidation = serverValidation && { level: ValidationLevel.error, messages: serverValidation } || undefined;
    const invalidOptions = mapInvalidOptions(cardValidation, valueName);

    return <AccessCard
      key={name}
      componentId={name}
      className={undefined}
      label={{ text: label as any }}
      options={defaultValue.defaultValueFields}
      valueItems={defaultValue.defaultValueFields}
      initialValueItems={undefined}
      validation={cardValidation}
      invalidOptions={invalidOptions}
      removeLabel={translation.remove}
      onChange={this.onChange}
      onDelete={this.removeGroup}
      useLabelOption={true}
      readOnly={readOnly} >
      {!this.props.readOnly && <OnClickOutside onClickOutside={this.closeSelectDialog}>
        <HotKeys keyMap={this.keyMap} handlers={this.handlers}>
          <div className={styles.linkSelector}>
            {this.props.handleClickAddButton && <div className={styles.cardValueAddFields}>
              <AddButton icon={MdAdd} onClick={this.openSelectDialog} required={false}>
                {translation.default_value_add_new_fields}
              </AddButton>
            </div>}
            {this.state.showSelectDialog && this.renderSelectDialog()}
          </div>
        </HotKeys>
      </OnClickOutside>}
    </AccessCard>;
  }
}
